import {Component, OnInit} from '@angular/core';
import {GenericSearchTableActionsDirective} from '@app/service-provider/util/generic-search-table-actions.directive';
import {DirectoryService, NameValueService, SearchServiceResponse, Service, ServiceAdministrationService} from '@gt-api';
import {ServiceTableEntry} from '@app/service-provider/service-number-search/service-table-entry';
import {MatDialog} from '@angular/material/dialog';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {Router} from '@angular/router';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {GtPageSizeService} from '@app/services/gt-page-size.service';
import {PageSizeTables} from '@app/models/enums/page-size-tables.enum';
import {PageEvent} from '@angular/material/paginator';
import {catchError, tap} from 'rxjs/operators';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {ConfirmDeleteDialogComponent} from '@app/shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {
  ServiceNumberDialogData,
  ServiceNumberEditorComponent
} from '@app/service-provider/service-number-editor/service-number-editor.component';
import {checkResponse} from '@app/shared/utils/app-utils';
import {EMPTY} from 'rxjs';
import {ServiceTemplateId} from '@app/models/enums/service-template-id.enum';
import {transition, trigger, useAnimation} from '@angular/animations';
import {fadeAnimation} from '@app/shared/animations/fade.animation';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';

@Component({
  templateUrl: './service-number-search.component.html',
  styleUrls: ['./service-number-search.component.scss', '../util/generic-search-table-actions.directive.scss'],
  animations: [trigger('fadeIn', [transition('* => shown', [useAnimation(fadeAnimation)])])]
})
export class ServiceNumberSearchComponent extends GenericSearchTableActionsDirective<ServiceTableEntry, Service> implements OnInit {
  dataSourceColumns = [];
  pageSize: number;

  private supportedPatterns = [ServiceTemplateId.MainNumber, ServiceTemplateId.AnswerGroup];
  private readonly defaultPageSize = 25;

  constructor(
    public dialog: MatDialog,
    private nameValueService: NameValueService,
    private authenticationService: GtAuthenticationService,
    private directoryService: DirectoryService,
    private router: Router,
    private serviceAdministrationService: ServiceAdministrationService,
    private notificationService: GtNotificationService,
    private translateService: TranslateService,
    private pageSizeService: GtPageSizeService
  ) {
    super();
  }

  ngOnInit(): void {
    this.dataSourceColumns = ['oname', 'number', 'name', 'pattern', 'actions'];
    this.pageSize = this.pageSizeService.getPageSize(PageSizeTables.ServiceNumberSearchTable, this.defaultPageSize);
  }

  showRow(entry: ServiceTableEntry) {
    const commands = ['/admin'];
    switch (entry.pattern) {
      case ServiceTemplateId.MainNumber:
        commands.push('main-numbers', 'strategy', entry.number);
        break;
      case ServiceTemplateId.AnswerGroup:
        commands.push('answer-groups', entry.number);
        break;
      case ServiceTemplateId.Menu:
        commands.push('menus', entry.number);
        break;
    }
    this.router.navigate(commands, {
      queryParams: {contactOname: entry.entry.organization}
    });
  }

  addRow() {
    if (this.hasAddServiceNumberRights()) {
      const dialogData: ServiceNumberDialogData = {
        service: {},
        editMode: false
      };

      this.openDialog(dialogData);
    }
  }

  editRow(entry: ServiceTableEntry) {
    if (this.hasEditServiceNumberRights()) {
      const dialogData: ServiceNumberDialogData = {
        service: {...entry.entry},
        editMode: true
      };

      this.openDialog(dialogData);
    }
  }

  saveRow(entry: ServiceTableEntry): void {}

  openDialog(dialogData: ServiceNumberDialogData) {
    const dialogRef = this.dialog.open(ServiceNumberEditorComponent, {
      minWidth: 500,
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(updateSearch => {
      if (updateSearch) {
        this.search(dialogRef.componentInstance.service.number);
      }
    });
  }

  newTableEntry(entry: Service, editing: boolean): ServiceTableEntry {
    return BuilderFactory.createBuilder<ServiceTableEntry>()
      .editing(editing)
      .oname(entry.organization)
      .name(entry.name)
      .number(entry.number)
      .pattern(entry.pattern)
      .entry(entry)
      .build();
  }

  search(searchString: string): void {
    this.startLoading();
    this.serviceAdministrationService
      .searchService1(undefined, undefined, GtPhoneNumberUtil.toBackend(searchString), searchString, searchString)
      .pipe(
        tap((response: SearchServiceResponse) => {
          if (this.stopLoading()) {
            const data = response.code === 200 ? response.services.filter(s => this.supportedPatterns.some(p => s.pattern === p)) : [];
            this.dataSource.data = data.map(e => this.newTableEntry(e, false));
          }
        }),
        catchError(error => {
          this.stopLoading();
          console.log('service number search failed', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  hasAddServiceNumberRights() {
    return this.authenticationService.serviceAccessRights.add;
  }

  hasEditServiceNumberRights() {
    return this.authenticationService.serviceAccessRights.edit;
  }

  hasDeleteServiceNumberRights() {
    return this.authenticationService.serviceAccessRights.delete;
  }

  openDeleteDialog(entry: ServiceTableEntry) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      autoFocus: false,
      data: entry.name
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteService(entry);
      }
    });
  }

  private deleteService(entry: ServiceTableEntry) {
    this.serviceAdministrationService
      .deleteService1(entry.number, entry.oname)
      .pipe(
        checkResponse(),
        tap(() => {
          this.search(this.filter.nativeElement.value);
          this.notificationService.showSuccessMessage();
        }),
        catchError(error => {
          console.log('delete service failed', error);
          this.notificationService.showErrorCustomMessage(this.translateService.instant('delete_sno.error'));
          return EMPTY;
        })
      )
      .subscribe();
  }

  paging(pageEvent: PageEvent) {
    this.pageSizeService.setPageSize(PageSizeTables.OrganizationSearchTable, pageEvent.pageSize);
  }
}
